import React, { useRef, useEffect } from 'react';
import {
  MDXEditor,
  type MDXEditorMethods,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  tablePlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  ConditionalContents,
  Separator,
  directivesPlugin,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { SupabaseClient } from '@supabase/supabase-js';
import { useMarkdownImageUpload } from '../../../utils/useMarkdownImageUpload';

// Move static configurations outside component
const EDITOR_CONFIG = {
  disableImports: true,
  spellCheck: true,
  autofocus: false,
  style: {
    fontSize: '16px',
    lineHeight: '1.5',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '1.5rem'
  }
} as const;

// Create a separate toolbar component to prevent re-renders
const EditorToolbar = React.memo(({ disabledPlugins = [], content = '', maxLength = 10000 }: { disabledPlugins?: string[], content?: string, maxLength?: number }) => (
  <>
    <div className="flex justify-between items-center w-full">
      <div className="flex items-center gap-2">
        <ConditionalContents
          options={[
            {
              fallback: () => (
                <>
                  <BlockTypeSelect />
                  <Separator />
                  <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
                  <Separator />
                  {/* Disable checkbox lists by only allowing bullet and number list types */}
                  <ListsToggle options={['bullet', 'number']} />
                  <InsertThematicBreak />
                  <Separator />
                  <CreateLink />
                  {!disabledPlugins.includes('image') && <InsertImage />}
                  <InsertTable />
                  <Separator />
                  <UndoRedo />
                </>
              )
            }
          ]}
        />
      </div>
      <div className={`text-sm ${content.length > maxLength ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
        {content.length}/{maxLength} characters
      </div>
    </div>
  </>
));

interface EventMarkdownEditorProps {
  content: string;
  onChange?: (content: string) => void;
  readOnly?: boolean;
  minHeight?: string;
  className?: string;
  eventId: string;
  supabase: SupabaseClient;
  placeholder?: string;
  disabledPlugins?: string[];
  maxLength?: number;
  onLengthChange?: (isOverLimit: boolean) => void;
}

export const EventMarkdownEditor: React.FC<EventMarkdownEditorProps> = ({
  content,
  onChange,
  eventId,
  supabase,
  readOnly = false,
  minHeight = '300px',
  className = '',
  placeholder = 'Start writing event details...',
  disabledPlugins = [],
  maxLength = 10000,
  onLengthChange
}) => {
  const editorRef = useRef<MDXEditorMethods>(null);
  
  // Get the image upload handler from the hook
  const { imageUploadHandler } = useMarkdownImageUpload({ supabase, eventId });
  
  // Add effect to fix z-index issue with modals
  useEffect(() => {
    // Ensure MDX editor popups are above the modal
    const popupContainers = document.querySelectorAll('.mdxeditor-popup-container');
    popupContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.zIndex = '2000';
      }
    });
  }, []);

  // Update the plugins array
  const editorPlugins = [
    headingsPlugin({
      allowedHeadingLevels: [1, 2, 3, 4, 5, 6]
    }),
    listsPlugin({
      styleList: true,
      // Disable checkbox lists
      checkList: false
    }),
    quotePlugin(),
    thematicBreakPlugin(),
    markdownShortcutPlugin(),
    tablePlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    ...(disabledPlugins.includes('image') ? [] : [imagePlugin({ imageUploadHandler })]),
    directivesPlugin(),
    toolbarPlugin({
      toolbarContents: () => <EditorToolbar disabledPlugins={disabledPlugins} content={content} maxLength={maxLength} />
    })
  ];

  return (
    <div className={`border border-gray-200 rounded-lg bg-white overflow-hidden ${className}`}>
      <MDXEditor
        ref={editorRef}
        markdown={content}
        onChange={(markdown) => {
          if (onChange && !readOnly) {
            onChange(markdown);
            onLengthChange?.(markdown.length > maxLength);
          }
        }}
        plugins={editorPlugins}
        readOnly={readOnly}
        contentEditableClassName={`prose prose-slate !max-w-none [&_ul]:!list-none [&_ol]:!list-none ${!readOnly ? `!min-h-[${minHeight}]` : ''} [&>div]:!mt-0 [&_.selected]:!bg-primary-50`}
        className={`w-full [&_.toolbar]:border-b [&_.toolbar]:border-gray-200 [&_.toolbar]:bg-gray-50 [&_.toolbar]:p-2 [&_button]:!text-gray-700 [&_button:hover]:!bg-gray-100 [&_button.active]:!bg-primary-50 [&_.prose]:!mt-0 [&_.prose]:!max-w-none [&_h1]:!text-3xl [&_h2]:!text-2xl [&_h3]:!text-xl [&_ul]:!list-disc [&_ol]:!list-decimal [&_blockquote]:!border-l-4 [&_blockquote]:!border-gray-300 [&_blockquote]:!pl-4 [&_blockquote]:!italic [&_pre]:!bg-gray-900 [&_pre]:!text-gray-100 [&_code]:!text-primary-dark [&_table]:!border-collapse [&_th]:!border [&_th]:!border-gray-300 [&_td]:!border [&_td]:!border-gray-200 [&_img]:!rounded-lg [&_a]:!text-primary-base [&_a:hover]:!text-primary-dark [&_hr]:!border-gray-300`}
        {...EDITOR_CONFIG}
        placeholder={placeholder}
      />
    </div>
  );
}; 